import { Link } from 'react-router-dom';
import PictureImage from '../Common/PictureImage';
import { IMAGE_ALT } from '../../seo/imageAltText';
import Footer from '../Layouts/Footer';
import Canonical from '../../seo/Canonical';
import './IntegrationsPage.css';
import IntegrationsHero from './IntegrationsHero';
import RelatedLinks from '../Common/RelatedLinks';
import { INTEGRATIONS_RELATED_BLOGS } from '../../data/resourcePageRelatedBlogs';

/** Simple Icons CDN — SVG marks for non-bundled brands */
function si(slug: string, color: string) {
  return `https://cdn.simpleicons.org/${slug}/${color.replace('#', '')}`;
}

/** Keep under /Images/... — a folder named `public/integrations/` becomes /integrations on the server and breaks SPA routing (Apache serves directory index instead of index.html). */
const AMAZON_MARK = `${import.meta.env.BASE_URL}Images/integration-marks/amazon.svg`;

type LogoTile = {
  name: string;
  slug: string;
  color: string;
  /** Use bundled asset instead of Simple Icons CDN (e.g. Amazon) */
  localSrc?: string;
};

type Category = {
  id: string;
  title: string;
  blurb: string;
  tiles: LogoTile[];
};

const categories: Category[] = [
  {
    id: 'amazon',
    title: 'Amazon',
    blurb: 'Amazon Seller Central, Amazon FBA, FBM, Buy Shipping and Seller Fulfilled Prime (SFP) integrations with real-time order sync, inventory management and Prime-ready fulfilment workflows.',
    tiles: [
      { name: 'Amazon Seller Central', slug: 'amazon', color: '#FF9900', localSrc: AMAZON_MARK },
      { name: 'Amazon FBA', slug: 'amazon', color: '#FF9900', localSrc: AMAZON_MARK },
      { name: 'Amazon FBM', slug: 'amazon', color: '#FF9900', localSrc: AMAZON_MARK },
      { name: 'Buy Shipping', slug: 'amazon', color: '#FF9900', localSrc: AMAZON_MARK },
      { name: 'Vendor Central', slug: 'amazon', color: '#FF9900', localSrc: AMAZON_MARK },
      { name: 'Multi-Channel Fulfilment', slug: 'amazon', color: '#FF9900', localSrc: AMAZON_MARK },
      { name: 'Seller Fulfilled Prime', slug: 'amazon', color: '#FF9900', localSrc: AMAZON_MARK },
      { name: 'Amazon Ads (feeds)', slug: 'amazon', color: '#FF9900', localSrc: AMAZON_MARK },
    ],
  },
  {
    id: 'shopify',
    title: 'Shopify',
    blurb: 'Shopify and Shopify Plus integration for DTC stores, B2B, subscriptions, real-time inventory sync and high-volume flash sale fulfilment.',
    tiles: [
      { name: 'Shopify', slug: 'shopify', color: '#7AB55C' },
      { name: 'Shopify Plus', slug: 'shopify', color: '#7AB55C' },
      { name: 'Shop Pay', slug: 'shopify', color: '#7AB55C' },
      { name: 'Shopify POS', slug: 'shopify', color: '#7AB55C' },
      { name: 'Shopify Markets', slug: 'shopify', color: '#7AB55C' },
      { name: 'Shopify Flow', slug: 'shopify', color: '#7AB55C' },
      { name: 'Headless (Storefront API)', slug: 'shopify', color: '#7AB55C' },
      { name: 'Shopify Collective', slug: 'shopify', color: '#7AB55C' },
    ],
  },
  {
    id: 'ebay',
    title: 'eBay',
    blurb: 'eBay integration with automated listings, real-time order synchronisation, managed payments, inventory updates and support for eBay International Shipping and global selling programmes.',
    tiles: [
      { name: 'eBay Seller Hub', slug: 'ebay', color: '#E53238' },
      { name: 'eBay UK & EU', slug: 'ebay', color: '#E53238' },
      { name: 'eBay Global Shipping', slug: 'ebay', color: '#E53238' },
      { name: 'Managed Payments', slug: 'ebay', color: '#E53238' },
      { name: 'Promoted Listings', slug: 'ebay', color: '#E53238' },
      { name: 'Business Seller', slug: 'ebay', color: '#E53238' },
      { name: 'Volume pricing', slug: 'ebay', color: '#E53238' },
      { name: 'Returns & cases', slug: 'ebay', color: '#E53238' },
    ],
  },
  {
    id: 'tiktok',
    title: 'TikTok',
    blurb: 'TikTok Shop integration for live commerce, creator-led campaigns, automated order sync, inventory updates and fast fulfilment that scales with every sale.',
    tiles: [
      { name: 'TikTok Shop', slug: 'tiktok', color: '#000000' },
      { name: 'TikTok Seller Center', slug: 'tiktok', color: '#000000' },
      { name: 'TikTok Live Sales', slug: 'tiktok', color: '#000000' },
      { name: 'Fulfilled by TikTok', slug: 'tiktok', color: '#000000' },
      { name: 'Creator storefronts', slug: 'tiktok', color: '#000000' },
      { name: 'UK & cross-border', slug: 'tiktok', color: '#000000' },
      { name: 'Campaign spikes', slug: 'tiktok', color: '#000000' },
      { name: 'Affiliate / partners', slug: 'tiktok', color: '#000000' },
    ],
  },
  {
    id: 'woocommerce',
    title: 'WooCommerce',
    blurb: 'WooCommerce integration for WordPress stores, custom plugins, B2B catalogues, real-time inventory sync and UK/EU VAT-ready fulfilment.',
    tiles: [
      { name: 'WooCommerce', slug: 'woocommerce', color: '#96588A' },
      { name: 'WordPress', slug: 'wordpress', color: '#21759B' },
      { name: 'Woo Subscriptions', slug: 'woocommerce', color: '#96588A' },
      { name: 'WooCommerce B2B', slug: 'woocommerce', color: '#96588A' },
      { name: 'Product bundles', slug: 'woocommerce', color: '#96588A' },
      { name: 'Multi-currency', slug: 'woocommerce', color: '#96588A' },
      { name: 'REST / webhooks', slug: 'woocommerce', color: '#96588A' },
      { name: 'Custom themes', slug: 'wordpress', color: '#21759B' },
    ],
  },
];

function LogoCard({ tile }: { tile: LogoTile }) {
  const src = tile.localSrc ?? si(tile.slug, tile.color);
  return (
    <div className="integration-logo-card">
      <div className="integration-logo-frame">
        <PictureImage src={src} alt={IMAGE_ALT.integrationsLogo(tile.name)} width={48} height={48} decoding="async" loading="lazy" />
      </div>
      <span>{tile.name}</span>
    </div>
  );
}

export default function IntegrationsPage() {
  return (
    <div className="integrations-page logistics-page">
      <Canonical href="https://www.pickpackpro.co.uk/integrations" />

      <IntegrationsHero />

      <div className="integrations-body">
        {categories.map((cat) => (
          <section key={cat.id} className="integrations-category" aria-labelledby={`cat-${cat.id}`}>
            <div className="integrations-category-head">
              <h2 className="integrations-category-title" id={`cat-${cat.id}`}>
                {cat.title}
              </h2>
              <p className="integrations-category-blurb">{cat.blurb}</p>
            </div>
            <div className="integrations-logo-grid">
              {cat.tiles.map((tile) => (
                <LogoCard key={`${cat.id}-${tile.name}`} tile={tile} />
              ))}
            </div>
          </section>
        ))}
      </div>

      <section className="integrations-cta-section">
        <Link to="/contact-fulfillment-services" className="integrations-cta-btn">
          Connect your store in minutes
        </Link>
        <p className="integrations-cta-note">
          Tell us where you sell — Amazon,{' '}
          <Link to="/blog/shopify-warehouse-integration">Shopify warehouse integration</Link>,{' '}
          <Link to="/blog/tiktok-shop-fulfilment-uk">TikTok Shop</Link>, eBay or WooCommerce — and we&apos;ll
          recommend the cleanest path. Many brands run{' '}
          <Link to="/blog/amazon-shopify-ebay-tiktok-one-3pl-uk">
            Amazon, Shopify, eBay &amp; TikTok through one 3PL
          </Link>{' '}
          with{' '}
          <Link to="/blog/multi-channel-fulfilment-uk">multi-channel fulfilment UK</Link>. See also{' '}
          <Link to="/blog/how-to-choose-multi-channel-fulfilment-partner-uk-2026">
            how to choose a multi-channel fulfilment partner
          </Link>
          .
        </p>
      </section>

      <RelatedLinks
        title="Related integration & fulfilment guides"
        serviceLinks={INTEGRATIONS_RELATED_BLOGS}
        blogLink={{ to: '/fulfillment-blog', label: 'All fulfilment blog articles' }}
      />

      <Footer />
    </div>
  );
}
